import ProfileWidget from '@shared/profile-widget'
import React, { useEffect, useState } from 'react'
import Table from '@components/shared/table'
import remove from 'lodash.remove'
import uniq from 'lodash.uniq'
import {
  CircularProgress,
  Paper,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { fetchDisplayedUsers } from '@store/actions/currently-displayed-users'
import { makeStyles } from 'tss-react/mui'
import { permissionListAlpha, selectRoleById } from '@store/selectors'
import {
  selectCurrentlyDisplayedUsers,
  selectTotalDisplayableUsers,
} from '@store/selectors/currently-displayed-users'
import { useAllActions, useSelector } from '@hooks'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

const useStyles = makeStyles()((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  permissions: {
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    width: '430px',
  },
  users: {
    width: '100%',
  },
  label: { cursor: 'pointer' },
}))

function Role() {
  const { id } = useParams()
  const theme = useTheme()
  const { classes } = useStyles()
  const { getRole, setRole, saveRole, setFlashMessage } = useAllActions()
  const dispatch = useDispatch()
  const [userRowsPerPage, setUserRowsPerPage] = useState(10)
  const [userPage, setUserPage] = useState(0)

  const role = useSelector((state) => selectRoleById(state, id))
  const permissions = useSelector(permissionListAlpha)
  const totalUsers = useSelector(selectTotalDisplayableUsers)
  const users = useSelector(selectCurrentlyDisplayedUsers)

  useEffect(() => {
    getRole(id)
  }, [])

  const isBarista = role?.role === 'barista'

  useEffect(() => {
    if (role) {
      dispatch(
        fetchDisplayedUsers(userPage, userRowsPerPage, { role_id: role.id }),
      )
    }
  }, [role, userRowsPerPage, userPage])

  if (!role || !permissions || (!users && !isBarista))
    return <CircularProgress />

  const togglePermission = (e, value) => {
    const permId = parseInt(e.target.name, 10)
    const permIds = [...role.permission_ids]

    if (value) {
      permIds.push(permId)
    } else {
      remove(permIds, (i) => i === permId)
    }

    setRole({ ...role, permission_ids: uniq(permIds) })
  }

  const updateRole = () => {
    saveRole(role)
      .then(() => setFlashMessage('Role Updated', 'success'))
      .catch((error) => setFlashMessage(error.message, 'error'))
  }

  const disabled = role.role === 'owner'

  return (
    <>
      <ProfileWidget
        buttons={[
          {
            title: 'Save Role',
            onClick: updateRole,
            color: theme.palette.primary.main,
          },
        ]}
        title={role.role}
      />
      <div className={classes.section}>
        <Paper className={classes.permissions}>
          <Typography variant='h6'>Permissions</Typography>
          {permissions.map((permission) => {
            return (
              <div key={permission.id}>
                <Switch
                  checked={role.permission_ids.includes(permission.id)}
                  disabled={disabled}
                  id={permission.id.toString()}
                  name={permission.id.toString()}
                  onChange={togglePermission}
                />
                <Tooltip title={permission.description}>
                  <label
                    className={classes.label}
                    htmlFor={permission.id.toString()}
                  >
                    {permission.permission}
                  </label>
                </Tooltip>
              </div>
            )
          })}
        </Paper>
        {!isBarista && (
          <div className={classes.users}>
            <Paper>
              <Table
                columns={[
                  { title: 'Name', field: 'name' },
                  { title: 'Email', field: 'email' },
                ]}
                count={totalUsers}
                data={users}
                onPageChange={setUserPage}
                onRowClick={(row) => `/users/${row.id}`}
                onRowsPerPageChange={setUserRowsPerPage}
                paginated
                title='Users'
              />
            </Paper>
          </div>
        )}
      </div>
    </>
  )
}

export default Role
